import { Plus, Maximize2 } from "lucide-react";
import { useState, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DocumentsTableContent } from './DocumentsTableContent';
import { DocumentsTableModal } from './DocumentsTableModal';
import { setLastEditedDocumentField } from '../../../store/reducers/index';
import { getDeclarationSummaryTranslation } from '../../../hooks/useLanguage';


const DocumentsTableComponent = ({
  declarationIndex,
  itemIndex,
  isSpanish,
  item,
  editableData,
  setEditableData,
  setIsDirty,
}) => {
  const [openDocModalIndex, setOpenDocModalIndex] = useState(null);
  const [openTipoModal, setOpenTipoModal] = useState({});
  const [isExpandModalOpen, setIsExpandModalOpen] = useState(false);
  const dispatch = useDispatch();
  const company = useSelector((state) => state.company);
  const userData = useSelector((state) => state.user.userData);
  const companyName = company?.nombre?.toLowerCase() || userData?.first_name?.toLowerCase();

  // Handler for adding Y025 document for TuDeFrigo
  const handleAddY025TuDeFrigo = () => {
    const updatedData = [...editableData];
    if (!updatedData[declarationIndex].partidas[itemIndex].documentos)
      updatedData[declarationIndex].partidas[itemIndex].documentos = [];
    updatedData[declarationIndex].partidas[itemIndex].documentos.push({
      codigo_documento: 'Y025',
      tipo: 1,
      numero: 'ESAEOF240000022A',
      fecha: '2025-10-30',
    });
    setEditableData(updatedData);
    setIsDirty(true);
    dispatch(setLastEditedDocumentField({
      field: '__documents__',
      value: null,
      declarationIndex,
      partidaIndex: itemIndex,
      documentIndex: updatedData[declarationIndex].partidas[itemIndex].documentos.length - 1,
    }));
  };

  // Handler for adding Y025 document for El Mosca
  const handleAddY025ElMosca = () => {
    const updatedData = [...editableData];
    if (!updatedData[declarationIndex].partidas[itemIndex].documentos)
      updatedData[declarationIndex].partidas[itemIndex].documentos = [];
    updatedData[declarationIndex].partidas[itemIndex].documentos.push({
      codigo_documento: 'Y025',
      tipo: 1,
      numero: 'ESAEOF240000022A',
      fecha: '2025-10-30',
    });
    setEditableData(updatedData);
    setIsDirty(true);
    dispatch(setLastEditedDocumentField({
      field: '__documents__',
      value: null,
      declarationIndex,
      partidaIndex: itemIndex,
      documentIndex: updatedData[declarationIndex].partidas[itemIndex].documentos.length - 1,
    }));
  };

  return (
    <div className="document-table">
      <div className="form-group flex-1 mt-12 !mb-0 flex justify-between flex-row items-center">
        <label htmlFor="companyName" className="!mb-0 !text-sm !opacity-100 !text-xl font-semibold uppercase">
          {!isSpanish ? "Documents" : "Documentos"} (
          {item.documentos?.length || 0})
        </label>
        <div className="flex gap-2 items-center">
          {/* Default add document button */}
          <button
            className="py-1 text-xs flex items-center gap-1"
            onClick={() => {
              const updatedData = [...editableData];
              if (!updatedData[declarationIndex].partidas[itemIndex].documentos)
                updatedData[declarationIndex].partidas[itemIndex].documentos = [];
              updatedData[declarationIndex].partidas[itemIndex].documentos.push({
                codigo_documento: "",
                tipo: "",
                numero: "",
                fecha: "",
              });
              setEditableData(updatedData);
              setIsDirty(true);
              dispatch(setLastEditedDocumentField({
                field: '__documents__',
                value: null,
                declarationIndex,
                partidaIndex: itemIndex,
                documentIndex: updatedData[declarationIndex].partidas[itemIndex].documentos.length - 1,
              }));
            }}
          >
            <Plus className={`size-4`} />
            {isSpanish ? "Agregar Documento" : "Add Document"}
          </button>
          {/* Client-specific Y025 buttons */}
          {companyName === 'tudefrigo' && (
            <button
              className="py-1.5 text-xs flex items-center gap-1 bg-secondary rounded-full pl-2 pr-3"
              onClick={handleAddY025TuDeFrigo}
              type="button"
            >
              <Plus className="size-4" />
              Agregar Y025
            </button>
          )}
          {companyName === 'el mosca' && (
            <button
              className="py-1.5 text-xs flex items-center gap-1 bg-secondary rounded-full pl-2 pr-3"
              onClick={handleAddY025ElMosca}
              type="button"
            >
              <Plus className="size-4" />
              Agregar Y025
            </button>
          )}
          {/* Expand View button */}
          <button
            className="bg-default text-white rounded-full py-1.5 px-3 text-xs flex items-center gap-1"
            onClick={() => setIsExpandModalOpen(true)}
            title={!isSpanish ? "Expand View" : "Vista Expandida"}
          >
            <Maximize2 className="size-3 stroke-white" />
            {!isSpanish ? "Expand View" : "Vista Expandida"}
          </button>
        </div>
      </div>
      <DocumentsTableContent
        declarationIndex={declarationIndex}
        itemIndex={itemIndex}
        isSpanish={isSpanish}
        item={item}
        editableData={editableData}
        setEditableData={setEditableData}
        setIsDirty={setIsDirty}
        openDocModalIndex={openDocModalIndex}
        setOpenDocModalIndex={setOpenDocModalIndex}
        openTipoModal={openTipoModal}
        setOpenTipoModal={setOpenTipoModal}
      />
      <DocumentsTableModal
        isOpen={isExpandModalOpen}
        onClose={() => setIsExpandModalOpen(false)}
        declarationIndex={declarationIndex}
        itemIndex={itemIndex}
        isSpanish={isSpanish}
        item={item}
        editableData={editableData}
        setEditableData={setEditableData}
        setIsDirty={setIsDirty}
      />
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders Only re-render if props that affect THIS component changed
const areDocumentsTablePropsEqual = (prevProps, nextProps) => {
  // Always re-render if these props change
  if (prevProps.declarationIndex !== nextProps.declarationIndex) return false;
  if (prevProps.itemIndex !== nextProps.itemIndex) return false;
  if (prevProps.isSpanish !== nextProps.isSpanish) return false;
  
  // Check if item prop changed (specifically documentos array)
  const prevItem = prevProps.item;
  const nextItem = nextProps.item;
  
  if (prevItem === nextItem) {
    // Check editableData for this item
    const prevDocumentos = prevProps.editableData?.[prevProps.declarationIndex]?.partidas?.[prevProps.itemIndex]?.documentos;
    const nextDocumentos = nextProps.editableData?.[nextProps.declarationIndex]?.partidas?.[nextProps.itemIndex]?.documentos;
    if (prevDocumentos !== nextDocumentos) return false;
    return true;
  }
  
  // Check if documentos array reference changed
  if (prevItem?.documentos !== nextItem?.documentos) return false;
  
  // Ignore function prop changes (they're recreated but functionally equivalent) setEditableData, setIsDirty are stable from parent
  
  return true; // Props are equal, skip re-render
};

export const DocumentsTable = memo(DocumentsTableComponent, areDocumentsTablePropsEqual);
